import React from 'react';
import JiraKeyChip from '../JiraKeyChip';
import MonthPicker from '../MonthPicker';
import { apiService } from '../../services/api';
import * as fmt from '../../lib/format';
import { GROUP_PAGE } from './model';
import { INTAKE_STAGE_LABEL, IntakeData, intakeStageStyle } from './types';
import { usePlanView } from './usePlanView';
import ViewState from './ViewState';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

interface Props {
  period: string;
  onPeriod: (value: string) => void;
  /** The latest month that can be picked. */
  max: string;
  projectKey: string | null;
  query: string;
  expanded: Set<string>;
  onToggleAll: (key: string) => void;
}

// What came in through the intake epics in a month, and what was carried in from earlier and is still open: each ask,
// where it stands, who raised it and how old it is.
export default function IntakeList({ period, onPeriod, max, projectKey, query, expanded, onToggleAll }: Props) {
  const { data, error, loading, reload } = usePlanView<IntakeData>(() => apiService.getPlanIntake(period), period);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const items = (data?.items ?? []).filter((i) => (!projectKey || i.project_key === projectKey) && words.every((w) => `${i.title} ${i.key} ${i.reporter ?? ''} ${i.project_name}`.toLowerCase().includes(w)));
  const byProject = new Map<string, typeof items>();
  for (const i of items) byProject.set(i.project_key, [...(byProject.get(i.project_key) ?? []), i]);
  const groups = Array.from(byProject, ([key, list]) => ({ key, name: list[0].project_name, list })).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <MonthPicker value={period} max={max} onChange={onPeriod} className="" />
        {data && <p className="min-w-0 flex-1 text-sm text-gray-700">{data.headline}</p>}
      </div>
      <ViewState loading={loading && !data} error={error} onRetry={reload} empty={data && groups.length === 0 ? 'No asks to show for this month.' : null}>
        {groups.map((g) => {
          const all = expanded.has(`intake:${g.key}`) || !!query || g.list.length <= GROUP_PAGE;
          const shown = all ? g.list : g.list.slice(0, GROUP_PAGE);
          return (
            <section key={g.key} className={`${CARD} mb-5 overflow-hidden`} aria-label={`${g.name} intake`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-5 pt-4 pb-2">
                <h2 className="min-w-0 break-words text-base font-semibold text-gray-900">{g.name}</h2>
                <span className="text-xs text-gray-600">{fmt.count(g.list.length)} {g.list.length === 1 ? 'ask' : 'asks'}</span>
              </div>
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[36rem] border-separate border-spacing-0 text-left text-sm">
                  <caption className="sr-only">{`${g.name} asks`}</caption>
                  <thead>
                    <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                      <th scope="col" className="sticky left-0 z-10 border-b border-gray-200 bg-white px-4 py-2 sm:min-w-[18rem]">Ask</th>
                      <th scope="col" className="border-b border-gray-200 px-3 py-2">Ticket</th>
                      <th scope="col" className="border-b border-gray-200 px-3 py-2">Where it stands</th>
                      <th scope="col" className="border-b border-gray-200 px-3 py-2">Raised by</th>
                      <th scope="col" className="border-b border-gray-200 px-3 py-2">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((i) => {
                      const style = intakeStageStyle(i.stage);
                      return (
                        <tr key={i.key} className="hover:bg-gray-50">
                          <th scope="row" className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-2.5 text-left align-top font-normal">
                            <span className="block max-w-[18rem] break-words leading-snug text-gray-900">{i.title}</span>
                            {i.carried && <span className="mt-1 block text-[0.6875rem] text-gray-600">Carried in from earlier</span>}
                          </th>
                          <td className="border-b border-gray-100 px-3 py-2.5 align-top"><JiraKeyChip issueKey={i.key} url={i.jira_url} /></td>
                          <td className="border-b border-gray-100 px-3 py-2.5 align-top">
                            <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${style.badge}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden />
                                {INTAKE_STAGE_LABEL[i.stage]}
                              </span>
                              {i.status && <span className="text-[0.6875rem] text-gray-600">{i.status}</span>}
                            </span>
                          </td>
                          <td className="border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">{i.reporter ?? <span aria-hidden>–</span>}</td>
                          <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 align-top tabular-nums text-gray-700">{i.age_days < 1 ? 'Today' : `${fmt.count(i.age_days)} d`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {g.list.length > GROUP_PAGE && !query && (
                <div className="border-t border-gray-100 px-5 py-2.5">
                  <button type="button" onClick={() => onToggleAll(`intake:${g.key}`)} className="rounded text-sm font-medium text-blue-700 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                    {expanded.has(`intake:${g.key}`) ? `Show the first ${GROUP_PAGE}` : `Show all ${fmt.count(g.list.length)} asks`}
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </ViewState>
    </>
  );
}
